import type {
    MouseEvent as ReactMouseEvent,
    TouchEvent as ReactTouchEvent
} from "react";
import { createRef, useEffect, useRef } from "react";

import { css } from "@compiled/react";

import { Adg4TabFC } from "./tab-helper";
import { Adg4TabListProps } from "./tab-list";
import { useAdg4TabsContext } from "./tabs";

const Adg4DraggableTabList: Adg4TabFC<Adg4TabListProps> = ({
    className,
    children,
    rowGap = "16px",
    ...props
}: Adg4TabListProps) => {
    const containerRef = createRef<HTMLDivElement>();
    const mouseDown = useRef(false);
    const startX = useRef(0);
    const scrollLeft = useRef(0);
    const canDrag = useRef(false);
    const { onKeyDown, tabListIsVisible, tabListScrollRef } =
        useAdg4TabsContext();

    const startDragging = (
        mouse?: ReactMouseEvent<HTMLUListElement>,
        touch?: ReactTouchEvent<HTMLUListElement>
    ) => {
        if (!canDrag.current) {
            return;
        }
        // Start listening for mouseup and mousemove events outside of the tab list
        document.addEventListener("mouseup", stopDragging);
        document.addEventListener("mousemove", move);

        mouseDown.current = true;
        startX.current =
            (touch && touch.touches[0]
                ? touch.touches[0].clientX
                : mouse?.clientX) ?? 0;
        scrollLeft.current = containerRef.current?.scrollLeft ?? 0;
    };

    const stopDragging = () => {
        if (!canDrag.current) {
            return;
        }
        // Stop listening for mouseup and mousemove events
        document.removeEventListener("mouseup", stopDragging);
        document.removeEventListener("mousemove", move);

        mouseDown.current = false;
    };

    const move = (
        mouse?: MouseEvent,
        touch?: ReactTouchEvent<HTMLUListElement>
    ) => {
        if (!canDrag.current || !mouseDown.current || !containerRef.current) {
            return;
        }
        // mouse.preventDefault();
        let dragAmount = 0;
        if (touch && touch.changedTouches[0]) {
            dragAmount = touch.changedTouches[0].clientX - startX.current;
        } else if (mouse) {
            dragAmount = mouse.clientX - startX.current;
        }

        containerRef.current.scrollLeft = scrollLeft.current - dragAmount;
    };

    // on resize, check if we can drag
    useEffect(() => {
        const updateCanDrag = () => {
            if (containerRef.current) {
                const container = containerRef.current;
                canDrag.current = container.scrollWidth > container.clientWidth;
            }
        };

        if (!containerRef.current) {
            return;
        }
        updateCanDrag();

        const observer = new ResizeObserver(() => {
            updateCanDrag();
        });

        observer.observe(containerRef.current);
        return () => {
            // Cleanup the observer by unobserving all elements
            observer.disconnect();
        };
    }, [containerRef]);

    const propsStyles = css({
        gap: rowGap
    });

    return (
        <div ref={containerRef} css={wrapperStyle}>
            <ul
                ref={tabListScrollRef}
                css={[
                    tabsContainerStyles,
                    propsStyles,
                    canDrag.current && tabListIsVisible && backAndForthAnimation
                ]}
                onTouchStart={(e) => startDragging(undefined, e)}
                onMouseDown={(e) => startDragging(e)}
                onTouchEnd={(e) => {
                    move(undefined, e);
                    stopDragging();
                }}
                onTouchMove={(e) => move(undefined, e)}
                onKeyDown={onKeyDown}
                className={className}
                tabIndex={-1}
                // Tech debt that should be fixed. See ldr/0012-jsx-a11y-strict.md for details.
                // eslint-disable-next-line jsx-a11y/no-noninteractive-element-to-interactive-role
                role="tablist"
                {...props}
            >
                {children}
            </ul>
        </div>
    );
};

const tabsContainerStyles = css({
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    flexWrap: "nowrap",
    padding: 0,
    margin: "0 auto",
    listStyle: "none",
    width: "fit-content",
    maxWidth: "100%"
});
const wrapperStyle = css({
    overflowX: "hidden"
});

// animation to demonstrate to user that section is draggable
const backAndForthAnimation = css({
    "@keyframes backAndForth": {
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        "0%": {
            transform: "translateX(0)"
        },
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        "50%": {
            transform: "translateX(calc(-60px))"
        },
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        "100%": {
            transform: "translateX(0)"
        }
    },
    "animation": "2s backAndForth forwards .5s"
});

Adg4DraggableTabList.tabsRole = "Adg4TabList";

export { Adg4DraggableTabList };
